import * as React from 'react';
import { Dispatch, Action } from 'redux';
import { connect } from 'react-redux';
import { SimpleResizeSensor } from '../SimpleResizeSensor';
import { Vector2D } from '../../../types/common';
import { Rectangle } from 'ark-common';
import { RawPointerEvent } from '../../../../../../common/src/ts/types/uievent';
import { isTouchEvent } from '../../../../../../common/src/ts/utils/uievent';
import { setPagesIgnorePointerEvents } from '../../../actions/UIModeAction';
import HoldToolIconButton from './HoldToolIconButton';

interface ToolbarItemScrollerProps {
  readonly vertical: boolean;
  readonly alignEnd: boolean;
  readonly className?: string;
  readonly style?: React.CSSProperties;
  readonly dispatch: Dispatch<Action>;
  readonly initialScrollAmount?: Vector2D;
  readonly scrollButtons?: boolean;
  readonly onUpdate?: (scrollAmount: Vector2D) => void;
}

interface ToolbarItemScrollerState {
  scrollAmount: Vector2D;
  offset: Vector2D;
}

const commonStyle: React.CSSProperties = {
  margin: 0,
  padding: 0,
  overflow: 'hidden',
  boxSizing: 'border-box',
  display: 'inline-flex',
  flex: '1 1 0%',
  alignItems: 'center',
  justifyContent: 'flex-start',
};

const scrollerStyle: React.CSSProperties = { ...commonStyle, position: 'relative' };
const wrapperStyle: React.CSSProperties = { ...commonStyle, overflow: 'visible' };

const capturePassive: Readonly<AddEventListenerOptions> = { capture: true, passive: true };
const captureNonPassive: Readonly<AddEventListenerOptions> = { capture: true, passive: false };

class ToolbarItemScroller extends React.PureComponent<
  ToolbarItemScrollerProps, ToolbarItemScrollerState
  > {
  private wrapper: HTMLElement | null = null;

  private touching: boolean = false;
  private dragging: boolean = false;
  private startPosition: Vector2D = { x: 0, y: 0 };
  private startScrollAmount: Vector2D = { x: 0, y: 0 };
  private scrollRange: Vector2D = { x: 0, y: 0 };

  public constructor(props: ToolbarItemScrollerProps) {
    super(props);

    const initialScrollAmount = this.props.initialScrollAmount
      ? this.props.initialScrollAmount
      : { x: 0, y: 0 };

    this.state = {
      scrollAmount: initialScrollAmount,
      offset: { x: 0, y: 0 },
    };
  }

  public componentDidMount() {
    const { wrapper } = this;
    if (wrapper === null) {
      return;
    }

    wrapper.addEventListener('click', this.preventClick, captureNonPassive);

    wrapper.addEventListener('mousedown', this.handlePointerDown, capturePassive);
    wrapper.addEventListener('touchstart', this.handlePointerDown, capturePassive);
    wrapper.addEventListener('pointerdown', this.handlePointerDown, capturePassive);

    document.addEventListener('mousemove', this.handlePointerMove, captureNonPassive);
    document.addEventListener('touchmove', this.handlePointerMove, captureNonPassive);
    document.addEventListener('pointermove', this.handlePointerMove, captureNonPassive);

    document.addEventListener('mouseup', this.handlePointerUp, captureNonPassive);
    document.addEventListener('touchend', this.handlePointerUp, captureNonPassive);
    document.addEventListener('pointerup', this.handlePointerUp, captureNonPassive);
    document.addEventListener('touchcancel', this.handlePointerUp, captureNonPassive);
    document.addEventListener('pointercancel', this.handlePointerUp, captureNonPassive);
  }

  public componentWillUnmount() {
    const { wrapper } = this;
    if (wrapper === null) {
      return;
    }

    wrapper.removeEventListener('click', this.preventClick, captureNonPassive);

    wrapper.removeEventListener('mousedown', this.handlePointerDown, capturePassive);
    wrapper.removeEventListener('touchstart', this.handlePointerDown, capturePassive);
    wrapper.removeEventListener('pointerdown', this.handlePointerDown, capturePassive);

    document.removeEventListener('mousemove', this.handlePointerMove, captureNonPassive);
    document.removeEventListener('touchmove', this.handlePointerMove, captureNonPassive);
    document.removeEventListener('pointermove', this.handlePointerMove, captureNonPassive);

    document.removeEventListener('mouseup', this.handlePointerUp, captureNonPassive);
    document.removeEventListener('touchend', this.handlePointerUp, captureNonPassive);
    document.removeEventListener('pointerup', this.handlePointerUp, captureNonPassive);
    document.removeEventListener('touchcancel', this.handlePointerUp, captureNonPassive);
    document.removeEventListener('pointercancel', this.handlePointerUp, captureNonPassive);
  }

  private preventClick = (ev: Event) => {
    if (this.dragging) {
      ev.preventDefault();
      ev.stopPropagation();
    }
  }

  public componentDidUpdate(_prevProps: Readonly<ToolbarItemScrollerProps>) {
    if (this.props.onUpdate) {
      this.props.onUpdate(this.state.scrollAmount);
    }
  }

  private onResize = (_clientRect: Rectangle) => {
    const { wrapper } = this;
    if (wrapper === null) {
      return;
    }

    const { alignEnd, vertical } = this.props;

    const parentRect = wrapper.parentElement!.getBoundingClientRect();
    const { offset } = this.state;

    const scrollRange = {
      x: wrapper.scrollWidth - parentRect.width,
      y: wrapper.scrollHeight - parentRect.height,
    };
    this.scrollRange = scrollRange;

    const currentAmount = this.state.scrollAmount;
    const scrollAmount = this.constrainScrollAmount(currentAmount, scrollRange, alignEnd);

    if (vertical) {
      this.setState({
        scrollAmount,
        offset: alignEnd ? { ...offset, y: -scrollRange.y } : { x: 0, y: 0 },
      });
    } else {
      this.setState({
        scrollAmount,
        offset: alignEnd ? { ...offset, x: -scrollRange.x } : { x: 0, y: 0 },
      });
    }
  }

  private getRawPointerPagePoint = (ev: RawPointerEvent): Vector2D => {
    if (isTouchEvent(ev)) {
      return { x: ev.changedTouches[0].pageX, y: ev.changedTouches[0].pageY };
    }
    return { x: ev.pageX, y: ev.pageY };
  }

  private handlePointerDown = (ev: RawPointerEvent) => {
    if (this.touching) {
      return;
    }

    this.touching = true;
    this.startPosition = this.getRawPointerPagePoint(ev);
    this.startScrollAmount = this.state.scrollAmount;
  }

  private constrainScrollAmount = (
    amount: Vector2D,
    range: Vector2D,
    alignEnd: boolean,
  ): Vector2D => {
    const { x: scrollX, y: scrollY } = amount;
    const { x: rangeX, y: rangeY } = range;

    if (alignEnd) {
      return {
        x: Math.min(Math.max(scrollX, 0), rangeX),
        y: Math.min(Math.max(scrollY, 0), rangeY),
      };
    }
    return {
      x: Math.min(Math.max(scrollX, -rangeX), 0),
      y: Math.min(Math.max(scrollY, -rangeY), 0),
    };
  }

  private handlePointerMove = (ev: RawPointerEvent) => {
    if (!this.touching) {
      return;
    }

    const { x, y } = this.getRawPointerPagePoint(ev);
    const { x: startX, y: startY } = this.startPosition;
    const { x: scrollX, y: scrollY } = this.startScrollAmount;
    const diffX = (x - startX);
    const diffY = (y - startY);

    if (
      !this.dragging
      && (Math.abs(diffX) >= 10 || Math.abs(diffY) >= 10)
    ) {
      this.dragging = true;
      setPagesIgnorePointerEvents(true, this.props.dispatch);
    }

    if (!this.dragging) {
      return;
    }

    ev.preventDefault();
    ev.stopPropagation();

    const { alignEnd } = this.props;
    const amount = { x: scrollX + diffX, y: scrollY + diffY };
    const scrollAmount = this.constrainScrollAmount(amount, this.scrollRange, alignEnd);

    this.setState({ scrollAmount });
  }

  private handlePointerUp = (ev: RawPointerEvent) => {
    if (this.dragging) {
      ev.preventDefault();
      ev.stopPropagation();

      const endDrag = () => {
        if (this.dragging) {
          this.dragging = false;
          setPagesIgnorePointerEvents(false, this.props.dispatch);
        }
      };
      // Prevent ghost click.
      setTimeout(endDrag, 300);
    }

    this.touching = false;
  }

  private handleScrollToStart = () => {
    this.doScroll(5);
  }

  private handleScrollToEnd = () => {
    this.doScroll(-5);
  }

  private doScroll(amount: number) {
    const { alignEnd } = this.props;
    const current = this.state.scrollAmount;
    const desired = { x: current.x + amount, y: current.y + amount };
    const scrollAmount = this.constrainScrollAmount(desired, this.scrollRange, alignEnd);
    this.setState({ scrollAmount });
  }

  public render() {
    const { vertical, scrollButtons, style, children } = this.props;

    const flexDirection = vertical ? 'column' : 'row';

    const { x: scrollX, y: scrollY } = this.state.scrollAmount;
    const { x: offsetX, y: offsetY } = this.state.offset;
    const translate = vertical ? offsetY + scrollY : offsetX + scrollX;
    const axis = vertical ? 'translateY' : 'translateX';
    const transform = { transform: `${axis}(${translate}px)` };

    const imageScrollStart = vertical ? 'scroll-up' : 'scroll-left';
    const imageScrollEnd = vertical ? 'scroll-down' : 'scroll-right';
    const enableScrollStart = (vertical ? scrollY : scrollX) !== 0;
    const enableScrollEnd = vertical ?
      (this.scrollRange.y > -scrollY) : (this.scrollRange.x > -scrollX);

    return (
      <section
        style={{ ...scrollerStyle, flexDirection, ...style }}>
        <SimpleResizeSensor onResize={this.onResize}></SimpleResizeSensor>
        {scrollButtons === true
          ? <HoldToolIconButton
              onHold={this.handleScrollToStart}
              disabled={!enableScrollStart}
              iconName={imageScrollStart} />
          : null}
        <div style={{ overflow: 'hidden' }}>
          <div
            style={{ ...wrapperStyle, flexDirection, ...transform, ...style }}
            ref={el => this.wrapper = el}>
            {children}
          </div>
        </div>
        {scrollButtons === true
          ? <HoldToolIconButton
            onHold={this.handleScrollToEnd}
            disabled={!enableScrollEnd}
            iconName={imageScrollEnd} />
          : null}
      </section>
    );
  }
}

export default connect()(ToolbarItemScroller);
